import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Users, Flame, HeartHandshake, HandHelping, Mail, X } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { toast } from "sonner";

const lastActive = (d) => (d === null ? "not yet" : d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`);
const prefill = (name) => `We've missed you this week, ${(name || "friend").split(" ")[0]}. No pressure at all — just wanted you to know we're praying for you and we're so glad you're part of this. Whenever you're ready, your Today page is waiting.`;

export default function Pulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudge, setNudge] = useState(null);
  const [msg, setMsg] = useState("");
  const [channels, setChannels] = useState({ inapp: true, email: false });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/engagement").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sendNudge = async () => {
    setSending(true);
    try {
      const chs = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
      const { data: res } = await portalApi.post("/portal/nudge", { member_id: nudge.id, message: msg, channels: chs.length ? chs : ["inapp"] });
      const parts = Object.entries(res.results).map(([k, v]) => `${k === "inapp" ? "in-app" : "email"}: ${v}`).join(", ");
      toast.success(`Nudge sent to ${nudge.name.split(" ")[0]} (${parts}).`);
      setNudge(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  if (loading) return <p className="text-[#8a7a6a] font-body" data-testid="portal-pulse">Gathering…</p>;
  if (!data) return <p className="text-[#8a7a6a] font-body" data-testid="portal-pulse">Nothing to show yet.</p>;

  const { aggregate: agg, members, is_admin } = data;
  const quiet = members.filter((m) => m.quiet);

  const NudgeBtn = ({ m }) => is_admin ? (
    <button data-testid={`nudge-btn-${m.id}`} onClick={() => { setNudge(m); setMsg(prefill(m.name)); setChannels({ inapp: true, email: false }); }}
      className="inline-flex items-center gap-1 text-[#A9724E] hover:text-[#925C3B] text-xs transition-colors">
      <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />nudge
    </button>
  ) : null;

  const Stat = ({ icon: Icon, value, label, testid }) => (
    <div data-testid={testid} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5">
      <Icon className="w-5 h-5 text-[#A9724E] mb-3" strokeWidth={1.5} />
      <p className="font-serif-display text-3xl text-[#2A2825]">{value}</p>
      <p className="text-sm text-[#5E4B3C] font-body">{label}</p>
    </div>
  );

  return (
    <div data-testid="portal-pulse" className="max-w-4xl">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">How we're showing up</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Cohort Pulse</h1>
      <p className="text-[#5E4B3C] font-body mb-10 leading-relaxed">
        Not to measure or compare — to notice each other. When someone goes quiet, that's an invitation to reach out.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat testid="pulse-members" icon={Users} value={agg.members} label="vessels in the cohort" />
        <Stat testid="pulse-active" icon={Activity} value={agg.active_7d} label="showed up this week" />
        <Stat testid="pulse-quiet" icon={HeartHandshake} value={agg.quiet} label={`quiet ${data.quiet_days}+ days`} />
        <Stat testid="pulse-streak" icon={Flame} value={agg.avg_streak} label="average streak" />
      </div>

      {quiet.length > 0 && (
        <section data-testid="pulse-quiet-list" className="bg-[#F3E9DE] border border-[#E0D0BC] rounded-xl p-6 md:p-8 mb-10">
          <div className="flex items-center gap-2 mb-4">
            <HeartHandshake className="w-5 h-5 text-[#A9724E]" strokeWidth={1.5} />
            <h2 className="font-serif-display text-2xl text-[#2A2825]">Gone quiet — send some love</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {quiet.map((m) => (
              <div key={m.id} className="inline-flex items-center gap-2 bg-white border border-[#E0D8C9] rounded-full pl-4 pr-3 py-2">
                <Link to={`/portal/profile/${m.id}`} data-testid={`quiet-${m.id}`} className="text-sm text-[#2A2825] hover:text-[#A9724E] transition-colors">
                  {m.name}<span className="text-[#8a7a6a] text-xs"> · {lastActive(m.last_active_days)}</span>
                </Link>
                <NudgeBtn m={m} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white/60 border border-[#E0D8C9] rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-6 py-3 border-b border-[#E0D8C9] bg-white/40 text-[10px] tracking-overline uppercase text-[#8a7a6a]">
          <span className="col-span-5">Vessel</span>
          <span className="col-span-2 text-center">Streak</span>
          <span className="col-span-2 text-center">Drops</span>
          <span className={is_admin ? "col-span-1 text-center" : "col-span-3 text-right"}>Last seen</span>
          {is_admin && <span className="col-span-1 text-center">Done</span>}
          {is_admin && <span className="col-span-1 text-center" title="Prayers received"><HandHelping className="w-3.5 h-3.5 inline" strokeWidth={1.5} /></span>}
        </div>
        {members.map((m) => (
          <div key={m.id} data-testid={`pulse-row-${m.id}`} className="grid grid-cols-12 gap-2 px-6 py-3.5 border-b border-[#E0D8C9]/60 last:border-0 items-center text-sm font-body">
            <span className="col-span-5">
              <Link to={`/portal/profile/${m.id}`} className="text-[#2A2825] hover:text-[#A9724E] transition-colors">{m.name}</Link>
              {m.quiet && <span className="ml-2 text-[10px] tracking-overline uppercase text-[#b45c5c]">quiet</span>}
              {is_admin && <span className="ml-3"><NudgeBtn m={m} /></span>}
            </span>
            <span className="col-span-2 text-center text-[#2A2825]">{m.streak > 0 ? <span className="inline-flex items-center gap-1 text-[#A9724E]"><Flame className="w-3.5 h-3.5" strokeWidth={1.5} />{m.streak}</span> : "—"}</span>
            <span className="col-span-2 text-center text-[#5E4B3C]">{m.drops}</span>
            <span className={`text-[#8a7a6a] ${is_admin ? "col-span-1 text-center" : "col-span-3 text-right"}`}>{lastActive(m.last_active_days)}</span>
            {is_admin && <span className="col-span-1 text-center text-[#5E4B3C]">{m.completion_rate}%</span>}
            {is_admin && <span className="col-span-1 text-center text-[#5E4B3C]">{m.prayers_received}</span>}
          </div>
        ))}
      </section>

      {nudge && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setNudge(null)}>
          <div data-testid="nudge-modal" className="bg-[#F4EFE6] rounded-2xl w-full max-w-lg p-6 md:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Send a gentle note</p>
                <h3 className="font-serif-display text-2xl text-[#2A2825]">to {nudge.name}</h3>
              </div>
              <button onClick={() => setNudge(null)} className="text-[#8a7a6a] hover:text-[#2A2825]"><X className="w-5 h-5" /></button>
            </div>
            <textarea data-testid="nudge-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E]" />
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm text-[#5E4B3C] cursor-pointer">
                <input data-testid="nudge-inapp" type="checkbox" checked={channels.inapp} onChange={(e) => setChannels((c) => ({ ...c, inapp: e.target.checked }))} />In-app
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5E4B3C] cursor-pointer">
                <input data-testid="nudge-email" type="checkbox" checked={channels.email} onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} />Email
              </label>
              <button data-testid="nudge-send" onClick={sendNudge} disabled={sending || !msg.trim() || (!channels.inapp && !channels.email)}
                className="ml-auto rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors disabled:opacity-50">
                {sending ? "Sending…" : "Send note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
